import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { UserCounts } from '@/api';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { FETCH_USER_COUNTS_QUERY_KEY } from './company-query-keys';

/**
 * The company-wide member, role and license counts. Computed by the server so
 * the client doesn't have to hold the whole roster to count it.
 */
export function useFetchUserCounts(companyId: string): {
    status: 'error' | 'success' | 'pending';
    counts: UserCounts | undefined;
    refetch: () => void;
} {
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const {
        status,
        data: counts,
        refetch
    } = useQuery({
        queryKey: [FETCH_USER_COUNTS_QUERY_KEY, companyId],
        queryFn: () => {
            return openApiRef.current.users
                .getUserCounts({ companyId })
                .then((response) => response.data.data.counts);
        },
        // There are no company-wide counts before a company is selected.
        enabled: companyId !== ''
    });

    return {
        status,
        counts,
        refetch: () => {
            void refetch();
        }
    };
}

/** Seeds the per-user cache from a read. */
